import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useModules } from "@/context/ModulesContext";
import api from "@/lib/api";
import {
  LayoutDashboard,
  Users,
  Sprout,
  CalendarDays,
  ReceiptText,
  UsersRound,
  Settings2,
  LogOut,
  Leaf,
  FileText,
  Package,
  Truck,
  Boxes,
  Wallet,
  Route,
  Timer,
  Calculator,
  ClipboardCheck,
  Gauge,
  Tablet,
  Wrench,
} from "lucide-react";

const groups = [
  { label: "", items: [{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" }] },
  {
    label: "Sales",
    items: [
      { to: "/estimates", label: "Estimates", icon: FileText, testid: "nav-estimates" },
      { to: "/quotes", label: "Quote Builder", icon: Calculator, testid: "nav-quotes" },
      { to: "/invoices", label: "Invoices", icon: ReceiptText, testid: "nav-invoices" },
      { to: "/leads", label: "Leads", icon: Sprout, testid: "nav-leads" },
      { to: "/catalog", label: "Catalog", icon: Package, testid: "nav-catalog", adminOnly: true },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/schedule", label: "Schedule", icon: CalendarDays, testid: "nav-schedule" },
      { to: "/dispatch", label: "Dispatch", icon: Truck, testid: "nav-dispatch" },
      { to: "/quality-checks", label: "Quality Checks", icon: ClipboardCheck, testid: "nav-quality", managerOnly: true },
      { to: "/cad", label: "Crew CAD", icon: Tablet, testid: "nav-cad" },
      { to: "/customers", label: "Customers", icon: Users, testid: "nav-customers" },
      { to: "/inventory", label: "Inventory", icon: Boxes, testid: "nav-inventory" },
      { to: "/equipment", label: "Equipment", icon: Wrench, testid: "nav-equipment" },
      { to: "/expenses", label: "Expenses", icon: Wallet, testid: "nav-expenses" },
      { to: "/mileage", label: "Mileage", icon: Route, testid: "nav-mileage" },
      { to: "/payroll", label: "Payroll", icon: Timer, testid: "nav-payroll" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/capacity", label: "Capacity & Mode", icon: Gauge, testid: "nav-capacity", adminOnly: true },
      { to: "/team", label: "Team", icon: UsersRound, testid: "nav-team", adminOnly: true },
      { to: "/settings", label: "Settings", icon: Settings2, testid: "nav-settings", adminOnly: true },
    ],
  },
];

const titles = {
  "/": "Dashboard",
  "/estimates": "Estimates",
  "/quotes": "Quote Builder",
  "/invoices": "Invoicing",
  "/leads": "Lead Pipeline",
  "/catalog": "Items & Services",
  "/schedule": "Schedule & Dispatch",
  "/dispatch": "Dispatch Board",
  "/capacity": "Capacity & Mode",
  "/quality-checks": "Lawn Quality Checks",
  "/customers": "Customers",
  "/inventory": "Inventory & Supplies",
  "/equipment": "Equipment & Fleet",
  "/mileage": "Mileage Log",
  "/expenses": "Expense Tracking",
  "/payroll": "Payroll & Hours",
  "/team": "Team",
  "/settings": "Settings",
};

export default function Layout() {
  const { user, logout, isAdmin, isManager } = useAuth();
  const { isVisible } = useModules();
  const moduleKey = (to) => (to === "/" ? "nav.dashboard" : "nav." + to.replace(/^\//, ""));
  const navigate = useNavigate();
  const loc = useLocation();
  const [logo, setLogo] = useState("");
  useEffect(() => { api.get("/business").then((r) => setLogo(r.data.logo_url || "")).catch(() => {}); }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const pageTitle = titles[loc.pathname] || (loc.pathname.startsWith("/customers/") ? "Customer" : "Dashboard");

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-primary text-primary-foreground fixed h-full">
        <div className="px-6 py-6 flex items-center gap-3 border-b border-white/10">
          {logo ? (
            <div className="h-11 w-11 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
              <img src={logo} alt="logo" className="h-full w-full object-contain" />
            </div>
          ) : (
            <div className="h-10 w-10 rounded-md bg-secondary flex items-center justify-center">
              <Leaf className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="leading-none">
            <div className="font-display font-extrabold text-base tracking-tight">Neighborhood</div>
            <div className="text-[0.7rem] text-primary-foreground/70 tracking-widest uppercase mt-1">Lawn Care CRM</div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-5 space-y-4 overflow-y-auto">
          {groups.map((g, gi) => {
            const visible = g.items.filter((n) => (!n.adminOnly || isAdmin) && (!n.managerOnly || isManager) && (n.to === "/settings" || isVisible(moduleKey(n.to))));
            if (visible.length === 0) return null;
            return (
              <div key={gi}>
                {g.label && (
                  <div className="px-3 mb-1.5 text-[0.6rem] uppercase tracking-[0.2em] text-primary-foreground/40 font-semibold">
                    {g.label}
                  </div>
                )}
                <div className="space-y-0.5">
                  {visible.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      data-testid={n.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                          isActive
                            ? "bg-secondary text-white"
                            : "text-primary-foreground/75 hover:bg-white/10 hover:text-white"
                        }`
                      }
                    >
                      <n.icon className="h-[18px] w-[18px]" />
                      {n.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center text-sm font-semibold uppercase">
              {(user?.name || "U").slice(0, 1)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[0.7rem] text-primary-foreground/60 capitalize">{user?.role}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="p-2 rounded-md hover:bg-white/10 transition-colors duration-200"
              title="Log out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 md:ml-64 flex flex-col min-w-0">
        <header className="h-16 border-b border-border bg-card flex items-center justify-between px-5 md:px-8 sticky top-0 z-20">
          <div>
            <div className="overline text-muted-foreground">Neighborhood Lawn Care LLC</div>
            <h1 className="font-display text-xl font-bold tracking-tight leading-none mt-0.5">{pageTitle}</h1>
          </div>
          <div className="md:hidden">
            <button onClick={handleLogout} data-testid="logout-btn-mobile" className="p-2 rounded-md hover:bg-muted">
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </header>
        <main className="flex-1 p-5 md:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
